import Head from 'next/head';
import Link from 'next/link';
import { useMemo } from 'react';
import { Star, PhoneCall } from 'lucide-react';
import dynamic from 'next/dynamic';
import path from 'path';
import fs from 'fs';
import { injectLocation } from '../../../../utils/injectLocation';
import { generateFAQSchema } from '../../../../utils/generateFAQSchema';
import { getDynamicLocalLine } from '../../../../utils/getLocalLine';
import { iconMap } from '../../../../utils/iconMap';
import { getImages } from '../../../../utils/getImages';
import { generateCostGuide } from "../../../../utils/generateCostGuide";

import allServices from '../../../../data/services/services';
import dynamicServices from '../../../../data/services/dynamic_services';
import geoCoverage from '../../../../data/geoCoverage';
import geoRemovedCoverage from '../../../../data/geoRemovedCoverage';
import cityMergeMap from '../../../../data/cityMergeMap';
import { serviceContent } from '../../../../data/serviceContent';
import { zipIntroMap } from '../../../../data/introByZip';

import { comparisonMap } from '../../../../data/seoModules/comparisonMap';
import { laPricingEngine } from "../../../../data/seoModules/laPricingEngine";
import { serviceFactor } from "../../../../data/seoModules/serviceFactor";
import { introVariants } from "../../../../data/seoModules/introVariants";
import zipProfile from "../../../../data/seoModules/zipProfile.json";
import decisionData from "../../../../data/seoModules/decision.json";


import Header from '../../../../components/Header';
import Hero from '../../../../components/Hero';
import Split from '../../../../components/Split';
import Benefits from '../../../../components/Benefits';
import ContentBlock from '../../../../components/ContentBlock';
import FAQ from '../../../../components/FAQ';
import RelatedLinks from '../../../../components/RelatedLinks';
import ProcessRoadmap from '../../../../components/ProcessRoadmap';
import CTASection from '../../../../components/CTASection';
import Gallery from '../../../../components/Gallery';
import LocationMap from '../../../../components/LocationMap';
import SocialProof from '../../../../components/SocialProof';
import ContactUs from '../../../../components/ContactUs';
import Footer from '../../../../components/Footer';
import QuickQuoteForm from '../../../../components/QuickQuoteForm';
import KnowledgeHub from '../../../../components/KnowledgeHub';


/* ----------------------------- Seed utilities ------------------------------ */

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return h >>> 0;
}

function pickFromPool(pool, seed, count = 1) {
  if (!pool || pool.length === 0) return [];
  const h = hashString(String(seed));
  const out = [];
  for (let i = 0; i < count; i++) {
    const idx = (h + i * 101) % pool.length;
    out.push(pool[idx]);
  }
  return out;
}

/* ---------------------------- Helpers and mappers --------------------------- */

function getServiceCategory(serviceData) {
  const slug = (serviceData?.link || '').toLowerCase().replace('/services/', '');
  const title = (serviceData?.title || '').toLowerCase();

  if (slug.includes('complete')) return 'complete';
  if (slug.includes('kitchen')) return 'kitchen';
  if (slug.includes('bath')) return 'bathroom';
  if (slug === 'adu') return 'adu';
  if (slug.includes('outdoor-living')) return 'outdoor';
  if (slug.includes('foundations')) return 'foundations';
  if (slug.includes('earthquake-retrofit')) return 'retrofit';
  if (slug.includes('exterior-painting')) return 'painting';
  if (slug.includes('energy-efficient-windows')) return 'windows';
  if (slug.includes('solar-panels')) return 'solar';
  if (slug.includes('artificial-turf')) return 'turf';
  if (slug.includes('hvac')) return 'hvac';
  if (slug.includes('pavers')) return 'pavers';
  if (slug.includes('asphalt-shingles')) return 'roof_asphalt';
  if (slug.includes('tile-roof')) return 'roof_tile';
  if (slug.includes('flat-roof')) return 'roof_flat';
  if (slug.includes('metal-roof')) return 'roof_metal';
  if (slug.includes('tpo-roof')) return 'roof_tpo';
  if (slug.includes('gutter')) return 'gutter';

  if (title.includes('kitchen')) return 'kitchen';
  if (title.includes('bath')) return 'bathroom';
  return 'complete';
}

function seededFloat(seed, min, max, step = 0.1) {
  const n = hashString(String(seed)) % Math.round((max - min) / step + 1);
  return Number((min + n * step).toFixed(1));
}

function getReviewBadge(zip) {
  const rating = seededFloat(zip + 'rating', 4.7, 5.0, 0.1);
  const count = 28 + (hashString(zip + 'rc') % 170);
  return { rating, count };
}

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function getRelatedLinks(city, zip, geoCoverageObj, maxCount = 10) {
  const related = [];
  const state = 'california';
  const cityZips = geoCoverageObj[state][city] || [];

  // 1️⃣ Other ZIPs in the same city
  const otherZips = cityZips.filter((z) => z !== zip);
  const sameCityCount = Math.min(otherZips.length, maxCount);
  shuffleArray(otherZips).slice(0, sameCityCount).forEach((z) => {
    related.push({ city, zip: z });
  });

  if (related.length >= maxCount) return related;

  // 2️⃣ Nearby ZIPs in other cities (closest numerically)
  const allZips = [];
  Object.entries(geoCoverageObj[state]).forEach(([c, zips]) => {
    if (c === city) return;
    zips.forEach((z) => allZips.push({ city: c, zip: z }));
  });

  // Sort by numeric distance
  allZips.sort((a, b) => Math.abs(parseInt(a.zip) - parseInt(zip)) - Math.abs(parseInt(b.zip) - parseInt(zip)));

  for (const { city: c, zip: z } of allZips) {
    related.push({ city: c, zip: z });
    if (related.length >= maxCount) break;
  }

  return related;
}

  //  Flip Blocks order - remove this function if you decide to remove it 
  function flipBySeed(seed, a, b) {
    const h = hashString(String(seed));
    return h % 2 === 0 ? [a, b] : [b, a];
  }

  function shuffleGroupBySeed(seed, components) {
    const arr = [...components];
    const h = hashString(String(seed));

    for (let i = arr.length - 1; i > 0; i--) {
      const j = (h + i * 13) % (i + 1);
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }

    return arr;
  }

/* --------------------------- External resources ---------------------------- */

function buildExternalLinks({ city, zip, serviceSlug }) {
  const formattedCity = city
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  // 1️⃣ Base external links
  const links = [
    {
      label: `California Contractors State License Board – Check a License`,
      href: `https://www.cslb.ca.gov/OnlineServices/CheckLicenseII/CheckLicense.aspx`,
      reason: `Verify contractor licensing for work in ${formattedCity}`,
      isOwned: true,
    },
    {
      label: `EPA Lead-Safe RRP Guidance for Renovations`,
      href: `https://www.epa.gov/lead/renovation-repair-and-painting-program`,
      reason: `Lead-safe rules for remodeling in homes built before 1978`,
      isOwned: true,
    },
    {
      label: `ENERGY STAR Rebate Finder for ${zip}`,
      href: `https://www.energystar.gov/rebate-finder?search=${zip}`,
      reason: `Find local rebates by ZIP for windows, HVAC, and appliances`,
      isOwned: true,
    },
    {
      label: `California Energy Commission – Building Standards`,
      href: `https://www.energy.ca.gov/programs-and-topics/programs/building-energy-efficiency-standards`,
      reason: `Official energy efficiency and Title 24 standards`,
      isOwned: true,
    },
    {
      label: `CalRecycle – Home Improvement Waste Guidelines`,
      href: `https://calrecycle.ca.gov/`,
      reason: `Recycling and disposal guidance for construction materials`,
      isOwned: true,
    },
    {
      label: `California Air Resources Board – Indoor Air Quality`,
      href: `https://ww2.arb.ca.gov/our-work/topics/indoor-air-quality-exposure`,
      reason: `Information on maintaining healthy indoor air during remodeling`,
      isOwned: true,
    },
    {
      label: `National Association of Home Builders (NAHB)`,
      href: `https://www.nahb.org/`,
      reason: `Best practices for remodeling projects and certified contractors`,
      isOwned: true,
    },
    // Owned / brand links (keep relevant)
    {
      label: `Google Business Profile – Modern Build`,
      href: `https://g.co/kgs/5HbGoXf`,
      reason: `Modern Build reviews and contact details`,
      isOwned: true,
    },
    {
      label: `Yelp Profile – Modern Build`,
      href: `https://www.yelp.com/biz/modern-build-home-remodeling-los-angeles`,
      reason: `Ratings and verified client photos`,
      isOwned: true,
    },
    {
      label: `Houzz Profile – Modern Build`,
      href: `https://www.houzz.com/pro/modernbuildhomeremodeling`,
      reason: `Project galleries and design inspiration`,
      isOwned: true,
    },
  ];

  // 2️⃣ Add service-specific resources
  const svc = (serviceSlug || "").toLowerCase();

  if (svc.includes("roof")) {
    links.push({
      label: `Cool Roof Rating Council Directory`,
      href: `https://coolroofs.org/directory`,
      reason: `Rated roofing products for energy performance`,
      isOwned: true,
    });
  }
  if (svc.includes("hvac")) {
    links.push({
      label: `AHRI Directory – Certified HVAC Equipment`,
      href: `https://www.ahridirectory.org/`,
      reason: `Find SEER2-certified HVAC systems for rebates and compliance`,
      isOwned: true,
    });
  }
  if (svc.includes("window")) {
    links.push({
      label: `NFRC Certified Products Directory`,
      href: `https://search.nfrc.org/`,
      reason: `U-Factor and SHGC ratings for windows and doors`,
      isOwned: true,
    });
  }
  if (svc.includes("bath") || svc.includes("kitchen") || svc.includes("remodel")) {
    links.push({
      label: `California Residential Code Overview (I-Codes)`,
      href: `https://codes.iccsafe.org/codes/united-states/california`,
      reason: `Model residential construction and remodeling codes`,
      isOwned: true,
    });
  }

  // 3️⃣ Deduplicate
  const seen = new Set();
  const uniqueLinks = links.filter((link) => {
    if (seen.has(link.href)) return false;
    seen.add(link.href);
    return true;
  });

  // 4️⃣ Shuffle to vary order (so it’s not the same every time)
  const seed = hashString(`${city}-${zip}-${serviceSlug}`);

  for (let i = uniqueLinks.length - 1; i > 0; i--) {
    const j = (seed + i * 17) % (i + 1);
    [uniqueLinks[i], uniqueLinks[j]] = [uniqueLinks[j], uniqueLinks[i]];
  }

  return uniqueLinks;
}

function ExternalResources({ city, zip, serviceData }) {
  const items = buildExternalLinks({
    city,
    zip,
    serviceSlug: serviceData?.link?.replace("/services/", ""),
  });

  const formattedCity = city
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  return (
    items?.length > 0 && (
      <div className="text-black text-center font-light bg-white leading-relaxed pt-2 pb-20 px-6 md:px-20 mx-auto">
        <h3 className="text-2xl mb-4 text-center">
          Helpful External Resources for {formattedCity}, CA {zip}
        </h3>
        <ul className="flex flex-wrap justify-center gap-3 underline">
          {items.slice(0, 7).map((item, idx) => (
            <li key={idx}>
              <a
                href={item.href}
                target="_blank"
                rel={item.isOwned ? "noopener noreferrer" : "noopener noreferrer nofollow"}
                className="hover:text-primary transition"
                aria-label={`Open ${item.label} in a new tab`}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    )
  );
}

/* ------------------------------- UI sections -------------------------------- */

function LocalizedMessage({ formattedCity, zip, localLine, serviceDataImage, serviceData }) {
  const reviewCount = 10 + (parseInt(zip.slice(-2), 10) % 20);
  const backgroundImage =
    serviceDataImage || '/hero/best-home-remodling-contractor-in-los-angeles.webp';

  return (
    <section
      className="relative bg-cover bg-center bg-no-repeat text-white text-center py-[120px]"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="absolute inset-0 bg-black/60 z-0" />
      <div className="relative z-10 max-w-[900px] mx-auto px-6 py-10">
        <div className="flex justify-center mb-6">
          {[...Array(5)].map((_, idx) => (
            <Star key={idx} className="w-6 h-6 mr-2 fill-primary text-transparent" />
          ))}
        </div>
        <p className="text-lg md:text-lg font-gilda leading-relaxed mb-4 max-w-4xl mx-auto">
          Rated <b>5.0</b> stars by trusted homeowners in <b>{formattedCity}, CA {zip}</b>.
          Trusted by over <b>{reviewCount}+</b> families this year.
        </p>
        {localLine && (
          <p className="text-lg md:text-lg font-gilda leading-relaxed max-w-2xl mx-auto text-white">
            {localLine} Looking for {serviceData.title} in {formattedCity}, CA? We've got you covered.
          </p>
        )}
      </div>
    </section>
  );
}

function ZipIntro({ zip, city, serviceData }) {
  const paragraph = zipIntroMap[zip];
  if (!paragraph) return null;

  const formattedCity = city
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  // First paragraph with the keyword
  const keywordText = `If you’re searching for ${serviceData.title} in ${formattedCity}, CA ${zip}, our team delivers trusted results for homeowners.`;

  // Split the original paragraph into sentences and group 2 per paragraph
  const otherParagraphs = (paragraph
    .split(/(?<=[.!?])\s+/)
    .reduce((acc, sentence, index) => {
      if (index % 2 === 0) {
        acc.push([sentence]);
      } else {
        acc[acc.length - 1].push(sentence);
      }
      return acc;
    }, [])
    .map((group) => group.join(" "))
  );

  // Combine keyword paragraph + the rest
  const formattedParagraphs = [keywordText, ...otherParagraphs];

  return (
    <section className="bg-white py-[100px] px-4">
      <div className="max-w-[900px] mx-auto text-center space-y-8">
        <div className="flex justify-center mb-4">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className="w-6 h-6 mr-2 fill-primary text-transparent"
            />
          ))}
        </div>

        <h3 className="uppercase tracking-[6px] text-lg text-gray font-light">
          Why We Love Working in This Area
        </h3>

        <h4 className="text-4xl font-light text-black leading-tight font-gilda">
          Homeowners in {formattedCity} {zip}, CA Trust Us
        </h4>

        {formattedParagraphs.map((text, i) => (
          <p
            key={i}
            className="text-gray font-light text-lg leading-relaxed max-w-4xl mx-auto"
          >
            {text}
          </p>
        ))}
      </div>
    </section>
  );
}

function PermitHowTo({ serviceData, city, zip, permits, count = 3 }) {
  const quotes = Array.isArray(permits) ? permits : [];
  const formattedCity = city
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <section className="bg-background py-[100px] px-4">
      <div className="max-w-[900px] mx-auto text-center space-y-8">
        <div className="flex justify-center mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
          ))}
        </div>
        <h3 className="uppercase tracking-[6px] text-lg text-gray font-light">
          Permits And Approvals
        </h3>
        <h4 className="text-xl sm:text-2xl font-light text-black leading-tight font-gilda">
          Permit Steps For {serviceData.title} in {formattedCity} {zip}
        </h4>
        <div className="max-w-4xl mx-auto w-full space-y-8">
          {quotes.slice(0, count).map((item, i) => (
            <div key={i} className="space-y-4 text-lg text-gray font-light leading-relaxed text-center">
              {[item.line1, item.line2, item.line3, item.line4, item.line5]
                .filter(Boolean)
                .map((line, index) => (
                  <div key={index} className="flex items-center justify-center w-full">
                    <span aria-hidden="true" className="shrink-0 w-4 border-t border-primary" />
                    <p className="mx-3 text-base sm:text-lg text-gray font-light leading-relaxed text-center max-w-[95%] sm:max-w-3xl">
                      {line}
                    </p>
                    <span aria-hidden="true" className="shrink-0 w-4 border-t border-primary" />
                  </div>
                ))}
            </div>
          ))}
          <p className="text-center text-sm text-gray mt-8">
            Tip, confirm details with your local building department before submitting.
          </p>
        </div>
      </div>
    </section>
  );
}

function ServiceBundles({ serviceData, city, zip }) {
  const formattedCity = city.split('-').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
  const cat = getServiceCategory(serviceData);

  const bundles = {
    kitchen: ['Energy efficient windows', 'HVAC', 'Flooring', 'Lighting plan'],
    bathroom: ['Ventilation upgrade', 'Tankless water heater', 'Low iron glass', 'Heated floors'],
    adu: ['Sound control', 'Windows', 'Roofing', 'HVAC mini split'],
    windows: ['Exterior painting', 'Insulation', 'HVAC'],
    hvac: ['Windows', 'Attic insulation', 'Smart thermostat'],
    pavers: ['Artificial turf', 'Outdoor lighting', 'Drainage'],
    painting: ['Windows', 'Gutters', 'Roofing repair'],
    retrofit: ['Foundation repair', 'Roofing', 'Gutters'],
    roof_asphalt: ['Gutters', 'Insulation', 'Solar ready conduit'],
    roof_tile: ['Gutters', 'Insulation', 'Solar ready conduit'],
    roof_flat: ['TPO', 'Drainage', 'Solar'],
    roof_metal: ['Insulation', 'Gutters', 'Solar'],
    roof_tpo: ['Drainage', 'Insulation', 'Solar'],
    turf: ['Pavers', 'Irrigation rework', 'Outdoor lighting'],
    outdoor: ['Pavers', 'Turf', 'Lighting'],
    foundations: ['Drainage', 'Gutters', 'Roofing'],
    solar: ['Roof inspection', 'Main panel upgrade', 'Battery ready'],
    complete: ['Windows', 'HVAC', 'Flooring', 'Lighting plan'],
  };

  const chipToSlug = {
    'Energy efficient windows': 'energy-efficient-windows',
    'HVAC': 'hvac',
    'Flooring': 'complete-home-remodeling',
    'Lighting plan': 'complete-home-remodeling',
    'Ventilation upgrade': 'hvac',
    'Tankless water heater': 'hvac',
    'Low iron glass': 'bathroom-remodeling',
    'Heated floors': 'bathroom-remodeling',
    'Sound control': 'adu',
    'Windows': 'energy-efficient-windows',
    'Roofing': 'asphalt-shingles',
    'Roofing repair': 'asphalt-shingles',
    'HVAC mini split': 'hvac',
    'Exterior painting': 'exterior-painting',
    'Insulation': 'complete-home-remodeling',
    'Smart thermostat': 'hvac',
    'Artificial turf': 'artificial-turf',
    'Outdoor lighting': 'outdoor-living',
    'Drainage': 'pavers',
    'Gutters': 'gutter',
    'Solar ready conduit': 'solar-panels',
    'Solar': 'solar-panels',
    'Irrigation rework': 'outdoor-living',
    'Roof inspection': 'asphalt-shingles',
    'Main panel upgrade': 'complete-home-remodeling',
    'Battery ready': 'solar-panels',
    'TPO': 'tpo-roof',
  };

  const list = bundles[cat] || bundles.complete;
  const chips = pickFromPool(list, zip, Math.min(5, list.length));

  return (
    <div className="text-black text-center font-light bg-white leading-relaxed pt-2 pb-20 px-20 mx-auto">
      <h3 className="text-2xl mb-4 text-center">Popular upgrades in {formattedCity}</h3>
      <ul className="flex flex-wrap justify-center gap-3 underline">
        {chips.map((label, idx) => {
          const slug = chipToSlug[label];
          const path = slug ? `/${slug}/california/${city}/${zip}` : `/service-areas/${serviceData.link.replace('/services/', '')}`;
          return (
            <li key={idx}>
              <Link href={path} prefetch={false} className="hover:text-primary transition" aria-label={`Explore ${label} in ${formattedCity}`}>
                {label}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Helper function to format long text into readable paragraphs
function formatTextBlocks(text) {
  if (!text) return [];

  const sentences = text.split(/(?<=[.!?])\s+/);

  return sentences
    .reduce((acc, sentence, index) => {
      if (index % 2 === 0) {
        acc.push([sentence]);
      } else {
        acc[acc.length - 1].push(sentence);
      }
      return acc;
    }, [])
    .map((group) => group.join(" "));
}

function LocalInsights({ serviceData, city, zip, localInsightParagraphs, count = 3 }) {
  const quotes = Array.isArray(localInsightParagraphs) ? localInsightParagraphs : [];
  const formattedCity = city.split('-').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');

  return (
    <section className="bg-background py-[100px] px-4">
      <div className="max-w-[900px] mx-auto text-center space-y-8">
        <div className="flex justify-center mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
          ))}
        </div>
        <h2 className="uppercase tracking-[6px] text-lg text-gray font-light">Local Insights For This Area</h2>
        <h3 className="text-4xl font-light text-black leading-tight font-gilda">
          Common Projects In {formattedCity}, CA {zip}
        </h3>
          {quotes.slice(0, count).map((item, i) => (
            <div key={i} className="text-gray font-light text-lg leading-relaxed max-w-4xl mx-auto space-y-2">
              {item.line1 && <p>{item.line1}</p>}
              {item.line2 && <p>{item.line2}</p>}
              {item.line3 && <p>{item.line3}</p>}
              {item.line4 && <p>{item.line4}</p>}
            </div>
          ))}
      </div>
    </section>
  );
}


function LocalGuide({ serviceData, city, zip, localGuideParagraphs, count = 3 }) {
  const quotes = Array.isArray(localGuideParagraphs) ? localGuideParagraphs : [];
  const formattedCity = city
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <section className="bg-white py-[50px] px-4">
      <div className="max-w-[900px] mx-auto text-center space-y-8">
        <div className="flex justify-center mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
          ))}
        </div>

        <h3 className="uppercase tracking-[6px] text-lg text-gray font-light">
          Local Guide
        </h3>

        <h4 className="text-4xl font-light text-black leading-tight font-gilda">
          Guide to {serviceData.title} in {formattedCity} {zip}
        </h4>

        <div className="space-y-6 text-lg text-gray font-light leading-relaxed text-center">
          {quotes.slice(0, count).map((item, i) => {
            const combinedText = [item.line1, item.line2, item.line3, item.line4]
              .filter(Boolean)
              .join(" ");

            const formattedParagraphs = formatTextBlocks(combinedText);

            return (
              <div key={i} className="space-y-4">
                {formattedParagraphs.map((text, idx) => (
                  <p key={idx}>{text}</p>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ChooseUs({ serviceData, city, zip, whyChooseUsParagraphs, count = 3 }) {
  const quotes = Array.isArray(whyChooseUsParagraphs) ? whyChooseUsParagraphs : [];
  const formattedCity = city
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <section className="bg-white py-[50px] px-4">
      <div className="max-w-[900px] mx-auto text-center space-y-8">
        <div className="flex justify-center mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
          ))}
        </div>
        <h3 className="uppercase tracking-[6px] text-lg text-gray font-light">Why Homeowners Choose Us</h3>
        <h4 className="text-4xl font-light text-black leading-tight font-gilda">
          Trusted Experts in {serviceData.title}
        </h4>
        <div className="space-y-4 text-lg text-gray font-light leading-relaxed text-center">
          {quotes.slice(0, count).map((item, i) => (
            <div key={i} className="space-y-2">
              {item.line1 && <p>{item.line1}</p>}
              {item.line2 && <p>{item.line2}</p>}
              {item.line3 && <p>{item.line3}</p>}
              {item.line4 && <p>{item.line4}</p>}
            </div>
          ))}
          <div className="text-center mt-12">
            <Link
              href={`/service-areas/${serviceData.link.replace('/services/', '')}`}
              className="inline-block text-lg uppercase py-3 px-8 bg-primary text-white font-base hover:bg-black transition-all duration-500 ease-in-out"
              aria-label={`Explore all ${serviceData.title} services in California`}
            >
              Explore All {serviceData.title} Services in California
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function MicroTestimonials({ service, city, zipcode, reviewsByZip, count = 3 }) {
  const quotes = reviewsByZip || [];

  const formattedCity = city
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <section className="bg-white py-[100px] px-4">
      <div className="max-w-[1200px] mx-auto text-center space-y-8">
        <div className="flex justify-center mb-4">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={24} className="fill-primary text-primary" />
          ))}
        </div>
        <h2 className="uppercase tracking-[6px] text-lg text-gray font-light">
          What Homeowners Say
        </h2>
        <h3 className="text-4xl font-light text-black leading-tight font-gilda">
          Around {formattedCity}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
          {quotes.slice(0, count).map((review, i) => (
            <div key={i} className="bg-white border border-[#f1eeeb] rounded-2xl p-6 shadow-sm">
              <div className="flex mb-3">
                {Array.from({ length: review.stars }).map((_, j) => (
                  <Star key={j} size={16} className="fill-primary text-primary" />
                ))}
              </div>
              <p className="text-gray-700 font-light leading-relaxed">{review.content}</p>
              <div className="mt-4 pt-3 border-t border-[#eae7e4] space-y-1">
                <div className="font-medium text-black">
                  {review.consumer} From {review.city}, {review.zipcode}
                </div>
                <div className="text-sm text-gray-500">
                  • {review.service} • {review.date}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- SEO INFO STRATAGY ------------------------------------ */

function getVariantIndex(city, zip, service, length) {
  const str = `${city}-${zip}-${service}`;
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  return Math.abs(hash) % length;
}

function pickVariant(group, city, zip, service) {
  const index = getVariantIndex(city, zip, service, group.length);
  return group[index];
}


function CostIntro({ service, city, zip, data }) {
  const p1 = pickVariant(introVariants.p1, city, zip, service)({
    service: data.name.toLowerCase(),
    city,
    zip,
    zipProfile,
  });

  const p2 = pickVariant(introVariants.p2, city, zip, service)({
    service: data.name.toLowerCase(),
    city,
    zip,
    zipProfile,
  });

  const P3 = pickVariant(introVariants.p3, city, zip, service)({
    service: data.name.toLowerCase(),
    city,
    zip,
    zipProfile,
  });

  const parts = P3.split(/(\{\{LOW\}\}|\{\{HIGH\}\})/g);

  return (
      <section className="py-12 md:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl items-center gap-10">
          {/* Content */}
          <div className="space-y-6">
            <span className="stars flex mb-5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
              ))}
            </span>
            <h2 className="uppercase tracking-[6px] text-lg text-gray font-light">Cost Overview and Key Pricing Factors</h2>
            <h3 className="text-4xl font-light text-black leading-tight font-gilda">Average {data.name} Cost in {city}, CA {zip}</h3>
            <p className="text-gray font-light text-lg leading-relaxed">{p1}</p>
            <p className="text-gray font-light text-lg leading-relaxed">{p2}</p>
            <p className="text-gray font-light text-lg leading-relaxed">
            {parts.map((part, i) => {
                if (part === "{{LOW}}") {
                  return (
                    <b key={i} className="font-semibold text-primary">
                      ${data.avgLow.toLocaleString()}
                    </b>
                  );
                }
                if (part === "{{HIGH}}") {
                  return (
                    <b key={i} className="font-semibold text-primary">
                      ${data.avgHigh.toLocaleString()}
                    </b>
                  );
                }
                return part;
              })}
            </p>
            <div className="flex items-center space-x-3 pt-4">
              <PhoneCall className="text-primary w-[40px] h-[40px]" />
              <div>
                <p className="text-sm text-gray capitalize">Free Consultation</p>
                <a href="tel:13233004130" className="text-2xl font-light text-primary hover:underline">
                  +1-323-300-4130
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
}

function CostTable({ data, city, zip }) {
  return (
    <section className="py-12 md:py-20 px-4 bg-background">
      <div className="container mx-auto max-w-5xl items-center gap-10">
        {/* Content */}
        <div className="space-y-6">
          <span className="stars flex mb-5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
            ))}
          </span>
          <h2 className="uppercase tracking-[6px] text-lg text-gray font-light">Estimated Costs by Project Size</h2>
          <h3 className="text-4xl font-light text-black leading-tight font-gilda">{data.name} Cost Breakdown in {city}, CA {zip}</h3>
          <p className="text-gray font-light text-lg leading-relaxed">To better understand {data.name.toLowerCase()} pricing in {city}, the table below outlines realistic cost ranges based on project size. These estimates reflect current contractor pricing trends, labor rates, and material costs specific to the {zip} area.</p>
          <p className="text-gray font-light text-lg leading-relaxed">Whether you're planning a small upgrade or a full-scale project, this breakdown helps you estimate total costs and compare pricing tiers across different project sizes.</p>
          <p className="text-gray font-light text-lg leading-relaxed">Average cost per {data.unit} in {city}:{" "}
            <b className="font-semibold text-primary">
              ${data.perUnitLow} – ${data.perUnitHigh}
            </b>
          </p>
          <div className="max-w-5xl mx-auto overflow-x-auto border border-primary mt-6 mb-10 shadow-sm">
            <table className="w-full text-sm md:text-base bg-white">
              <thead>
                <tr className="bg-primary">
                  <th className="p-3 md:p-4 text-left text-primary text-lg leading-relaxed font-semibold">Project Size ({data.unit})</th>
                  <th className="p-3 md:p-4 text-left text-primary text-lg leading-relaxed font-semibold">Estimated Cost Range</th>
                </tr>
              </thead>

              <tbody>
                {data.table.map((row, i) => (
                  <tr key={i} className="border-t border-background hover:bg-background transition-colors">
                    <td className="p-3 md:p-4 text-gray font-light text-lg leading-relaxed">{row.size}</td>
                    <td className="p-3 md:p-4 text-gray font-light text-lg leading-relaxed">
                      ${row.low.toLocaleString()} – ${row.high.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function Factors({ service, city, zip, data }) {
  const key = service?.includes("/services/")
    ? service.replace("/services/", "")
    : service;

  const factors = serviceFactor[key] || serviceFactor["complete-home-remodeling"] || [];

  return (
    <section className="py-12 md:py-20 px-4 bg-white">
      <div className="container mx-auto max-w-5xl items-center gap-10">
        {/* Content */}
        <div className="space-y-6">
          <span className="stars flex mb-5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
            ))}
          </span>

          <h2 className="uppercase tracking-[6px] text-lg text-gray font-light">Key Cost Drivers and Pricing Factors</h2>
          <h3 className="text-4xl font-light text-black leading-tight font-gilda">What Affects {data.name} Cost in {city}, CA {zip}</h3>

          <p className="text-gray font-light text-lg leading-relaxed">
            Several key factors determine the final {data.name.toLowerCase()} cost in {city}. Understanding these variables will help you plan your budget more accurately and avoid unexpected expenses during your project.
          </p>

          {factors.map((f, i) => (
            <div key={i} className="border-l-4 border-primary pl-4 md:pl-6 py-2">
              <h4 className="mt-4 text-primary font-semibold text-lg leading-relaxed">
                {i + 1}. {f.title}
              </h4>
              <p className="mt-4 text-gray font-light text-lg leading-relaxed">
                {f.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PRICE FORMAT ---------------- */

function formatPrice(value) {
  if (value < 100) return `$${value.toFixed(2)}/sqft`;
  if (value < 10000) return `$${Math.round(value).toLocaleString()}`;
  return `$${(value / 1000).toFixed(0)}k`;
}

/* ---------------- COMPONENT ---------------- */
const bestFitMap = {
  "asphalt-shingles": "Best for most homeowners looking for affordability and solid durability",
  "tile-roof": "Best for high-end homes needing maximum lifespan and premium look",
  "metal-roof": "Best for heat reflection, wildfire resistance, and energy efficiency",
  "flat-roof": "Best for modern homes and low slope structures with budget control",
  "tpo-roof": "Best for commercial style energy efficient flat roofing systems",
};

function Comparison({ service, city, zip }) {
  const config = comparisonMap[service] || comparisonMap.default;
  const isRoofing = config.category === "roofing";

  return (

      <section className="py-12 md:py-20 px-4 bg-background">
      <div className="container mx-auto max-w-5xl items-center gap-10">
        {/* Content */}
        <div className="space-y-6">
          <span className="stars flex mb-5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
            ))}
          </span>
          <h2 className="uppercase tracking-[6px] text-lg text-gray font-light">Service Options and Cost Comparison</h2>
          <h3 className="text-4xl font-light text-black leading-tight font-gilda">{config.title} in {city}, CA {zip}</h3>
          <p className="text-gray font-light text-lg leading-relaxed">Comparing different {config.title.toLowerCase()} options in {city} helps you understand cost differences, lifespan expectations, and which solution best fits your property. Pricing below reflects real local conditions including labor rates, material costs, and demand in the {zip} area.</p>

          <div className="max-w-5xl mx-auto overflow-x-auto border border-primary mt-6 mb-10 shadow-sm">
            <table className="w-full text-sm md:text-base bg-white">
              <thead>
                <tr className="bg-primary">
                  <th className="p-3 md:p-4 text-left text-primary text-lg leading-relaxed font-semibold">Type</th>
                  <th className="p-3 md:p-4 text-left text-primary text-lg leading-relaxed font-semibold">Cost Range (Adjusted)</th>
                  <th className="p-3 md:p-4 text-left text-primary text-lg leading-relaxed font-semibold">Lifespan</th>
                  <th className="p-3 md:p-4 text-left text-primary text-lg leading-relaxed font-semibold">Best For</th>
                </tr>
              </thead>

              <tbody>
              {config.rows.map((item, i) => {
                const result = laPricingEngine(
                  { low: item.costLow, high: item.costHigh },
                  city,
                  zip
                );

                return (
                  <tr key={i} className="border-t border-background hover:bg-background transition-colors">
                    <td className="p-3 md:p-4 text-gray font-light text-lg leading-relaxed">{item.type}</td>
                    <td className="p-3 md:p-4 text-gray font-light text-lg leading-relaxed">
                      {formatPrice(result.low)} – {formatPrice(result.high)}
                    </td>
                    <td className="p-3 md:p-4 text-gray font-light text-lg leading-relaxed">{item.life}</td>
                    <td className="p-3 md:p-4 text-gray font-light text-lg leading-relaxed whitespace-pre-line">{item.best}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {isRoofing && (
          <div className="container space-y-6 mx-auto max-w-5xl items-center gap-10 py-12 md:py-20">
            <h3 className="text-4xl font-light text-black leading-tight font-gilda">Which Roofing System Is Best for {city} Homes?</h3>
            <p className="text-gray font-light text-lg leading-relaxed">This comparison focuses on long-term value in {city}, including heat resistance, wildfire safety, maintenance requirements, and total lifetime cost.</p>

            <div className="max-w-5xl mx-auto overflow-x-auto border border-primary mt-6 mb-10 shadow-sm">
              <table className="w-full text-sm md:text-base bg-white">
                <thead>
                  <tr className="bg-primary">
                    <th className="p-3 md:p-4 text-left text-gray text-lg leading-relaxed font-semibold">System</th>
                    <th className="p-3 md:p-4 text-left text-gray text-lg leading-relaxed font-semibold">Why Choose It</th>
                    <th className="p-3 md:p-4 text-left text-gray text-lg leading-relaxed font-semibold">Cost Level</th>
                    <th className="p-3 md:p-4 text-left text-gray text-lg leading-relaxed font-semibold">Best Fit</th>
                  </tr>
                </thead>

                <tbody>
                  {Object.entries(comparisonMap)
                    .filter(([_, v]) => v.category === "roofing")
                    .map(([key, group]) => {
                      const avg =
                        group.rows.reduce(
                          (acc, r) => acc + (r.costLow + r.costHigh) / 2,
                          0
                        ) / group.rows.length;

                      const result = laPricingEngine(
                        { low: avg, high: avg },
                        city,
                        zip
                      );

                      return (
                        <tr key={key} className="border-t border-[#f8f5f0] hover:bg-[#f8f5f0] transition-colors">
                          <td className="p-3 md:p-4 text-left text-primary text-lg leading-relaxed font-light">
                            {group.title}
                          </td>

                          <td className="p-3 md:p-4 text-left text-gray text-lg leading-relaxed font-light">
                            {key === "asphalt-shingles" &&
                              "Best balance of cost and durability"}
                            {key === "tile-roof" &&
                              "Longest lifespan and premium curb appeal"}
                            {key === "metal-roof" &&
                              "Best heat resistance and energy efficiency"}
                            {key === "flat-roof" &&
                              "Lowest cost for modern flat designs"}
                            {key === "tpo-roof" &&
                              "Best for energy efficient flat roofs"}
                          </td>

                          <td className="p-3 md:p-4 text-left text-gray text-lg leading-relaxed font-light">
                            {formatPrice(result.low)} – {formatPrice(result.high)} avg
                          </td>

                          <td className="p-3 md:p-4 text-left text-gray text-lg leading-relaxed font-light">
                            {bestFitMap[key] || "Depends on budget, structure, and design goals"}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </section>
  );
}


function Formula({ data, city }) {
  if (!data?.formula || !data?.example) return null;

  return (
    <section className="bg-white py-12 md:py-20 px-4">
      <div className="max-w-[900px] mx-auto text-center space-y-8">

        <div className="space-y-6">
          <span className="stars flex mb-5 justify-center">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
            ))}
          </span>

          <h3 className="uppercase tracking-[6px] text-lg text-gray font-light">
            Cost Estimation Formula
          </h3>

          <h4 className="text-4xl font-light text-black leading-tight font-gilda">
            How to Estimate {data.name} Cost in Your Area
          </h4>


          <p className="text-center text-gray font-light text-lg leading-relaxed">
            A simple way to estimate {data.name.toLowerCase()} cost in {city} is by using a standard pricing formula. Contractors across {city}, CA commonly use this method to calculate project costs quickly and consistently.
          </p>

          <div className="text-center">
            <div className="bg-white border-2 border-primary rounded-lg p-4 inline-block mb-6 shadow-sm">
              <code className="text-sm md:text-base text-primary font-light">
                {data.formula.label}
              </code>
            </div>
          </div>

          <p className="text-center text-gray font-light text-lg leading-relaxed">
            Typical cost per {data.unit}:{" "}
            <b className="font-semibold text-primary">
              ${data.perUnitLow} – ${data.perUnitHigh}
            </b>
          </p>

          <div className="bg-white border border-primary rounded-lg p-5 md:p-6 shadow-sm">

            <p className="mb-2 text-center text-primary font-semibold text-lg leading-relaxed">
              {data.formula.exampleLabel}
            </p>

            <p className="text-center text-gray font-light text-lg leading-relaxed">
              Example calculation based on average {data.name.toLowerCase()} pricing in your area:
            </p>

            <p className="mt-3 text-center text-gray font-light text-lg leading-relaxed">
              {data.example.size} {data.unit} × Cost Per {data.unit}
            </p>

            <p className="mt-2 text-center text-primary font-semibold text-xl md:text-2xl leading-relaxed">
              = ${data.example.low.toLocaleString()} – ${data.example.high.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
     </section>
  );
}

function Decision({ data, city, zip, service }) {
  const decision = decisionData?.[zip]?.services?.[service];

  if (!decision) return null;

  return (
    <section className="py-12 md:py-20 px-4 bg-background">
      <div className="container mx-auto max-w-5xl items-center gap-10">
        
        <div className="space-y-6">

          <span className="stars flex mb-5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 mr-2 fill-primary text-transparent" />
            ))}
          </span>

          <h2 className="uppercase tracking-[6px] text-lg text-gray font-light">
            Choosing the Right Option for Your Project
          </h2>

          <h3 className="text-4xl font-light text-black leading-tight font-gilda">
            How to Choose the Right {data.name} Option in {city}
          </h3>

          {/* ✅ dynamic content */}
          <p className="text-gray font-light text-lg leading-relaxed">
            {decision.intro}
          </p>

          <p className="text-gray font-light text-lg leading-relaxed">
            {decision.cost}
          </p>

          <p className="text-gray font-light text-lg leading-relaxed">
            {decision.permits}
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-6 text-gray font-light text-lg leading-relaxed">
            {decision.bullets.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>

          <p className="text-gray font-light text-lg leading-relaxed">
            {decision.recommendation}
          </p>

          {/* CTA stays same */}
          <div className="flex items-center space-x-3 pt-4">
            <PhoneCall className="text-primary w-[40px] h-[40px]" />
            <div>
              <p className="text-sm text-gray capitalize">Free Consultation</p>
              <a href="tel:13233004130" className="text-2xl font-light text-primary hover:underline">
                +1-323-300-4130
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}



/* --------------------------------- Page ------------------------------------ */

export default function ServiceLandingPage({
  content,
  galleryImages,
  serviceData,
  city,
  zip,
  relatedServices,
  localLine,
  relatedLinks,
  reviewsByZip,
  whyChooseUsParagraphs,
  permits,
  localGuideParagraphs,
  localInsightParagraphs,
  costGuide,
  mergedFaqs = [],
  renderedSections, // ✅ ADD THIS LINE
  groupBOrder, // ✅ Receive the order
}) {
  const formattedCity = city
  .split('-')
  .map((w) => w[0].toUpperCase() + w.slice(1))
  .join(' ');


  const injectDynamic = (text, isSplitImage = false) => {
    let citySlug = city ? city.toLowerCase().trim().replace(/\s+/g, '-') : '';
    
    // Override citySlug for split images
    if (isSplitImage && citySlug === 'la-cañada-flintridge') {
      citySlug = 'la-canada-flintridge';
    }

    return text
      ?.replace(/\[CITY\]/g, formattedCity)
      .replace(/\[ZIP\]/g, zip)
      .replace(/\[CITY_SLUG\]/g, citySlug);
  };

  const serviceType = serviceData.link.replace("/services/", "");
  const knowledgeSeed = `${serviceType}-${city}-${zip}`;
  const currentPath = `/${serviceType}/california/${city}/${zip}`;
  const faqKey = `${serviceType}-${city}-${zip}`;

  const contentWithLocation = {
    ...content,
    meta: {
      ...content.meta,
      title: injectDynamic(content.meta.title),
      description: injectDynamic(content.meta.description),
    },
    split: {
      ...content.split,
      title: injectDynamic(content.split.title),
      description: injectDynamic(content.split.description),
      extraText: injectDynamic(content.split.extraText),
    },
    benefits: {
      ...content.benefits,
      title: injectDynamic(content.benefits.title),
      subtitle: injectDynamic(content.benefits.subtitle),
      items: content.benefits.items.map((item) => ({
        ...item,
        icon: iconMap[item.icon],
        title: injectDynamic(item.title),
        description: injectDynamic(item.description ?? ''),
        href: item.href ?? null,
      })),
    },
    contentBlock: {
      ...content.contentBlock,
      title: injectDynamic(content.contentBlock.title),
      subtitle: injectDynamic(content.contentBlock.subtitle),
      description: injectDynamic(content.contentBlock.description),
      extraText: injectDynamic(content.contentBlock.extraText),
    },
    processSteps: content.processSteps.map((step) => ({
      ...step,
      icon: iconMap[step.icon],
      title: injectDynamic(step.title),
      description: injectDynamic(step.description),
    })),
    secondSplit: {
      ...content.secondSplit,
      title: injectDynamic(content.secondSplit.title),
      subtitle: injectDynamic(content.secondSplit.subtitle),
      description: injectDynamic(content.secondSplit.description),
      extraText: injectDynamic(content.secondSplit.extraText),
    },
  };


  const heroImage = serviceData.imageSrc || '/hero/best-home-remodling-contractor-in-los-angeles.webp';


// ✅ Render Benefits & ProcessRoadmap client-side with proper icon mapping
  const benefitsComponent = (
    <Benefits
      key="benefits"
      {...contentWithLocation.benefits}
    />
  );

  const processComponent = (
    <ProcessRoadmap
      key="process"
      title="Our Process"
      subtitle="Step by step from concept to completion"
      steps={contentWithLocation.processSteps}
    />
  );

  // ✅ Apply the order from server
  const groupBComponents = groupBOrder[0] === 'benefits' 
    ? [benefitsComponent, processComponent]
    : [processComponent, benefitsComponent];


  return (
    <div key={`${city}-${zip}`}>
      <Head>
        <title>{contentWithLocation.meta.title}</title>
        <meta name="description" content={contentWithLocation.meta.description} />
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
        <meta httpEquiv="content-language" content="en-US" />
        <meta name="author" content="Modern Build Home Remodeling" />

        {(() => {
          const svc = serviceData.link.replace('/services/', '');
          const path = `${svc}/california/${city}/${zip}`;
          const absUrl = `https://modernbuildremodel.com/${path}`;
          return (
            <>
              <link rel="canonical" href={absUrl} />
              <link rel="alternate" hrefLang="en-US" href={absUrl} />
              <link rel="alternate" hrefLang="x-default" href={absUrl} />
              <meta property="og:url" content={absUrl} />
            </>
          );
        })()}

        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={contentWithLocation.meta.title} />
        <meta property="og:description" content={contentWithLocation.meta.description} />
        <meta property="og:image" content={`https://modernbuildremodel.com${heroImage}`} />
        <meta property="og:image:alt" content={`${serviceData.title} in ${formattedCity} CA ${zip}`} />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={contentWithLocation.meta.title} />
        <meta name="twitter:description" content={contentWithLocation.meta.description} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${heroImage}`} />
        <meta name="twitter:image:alt" content={`${serviceData.title} in ${formattedCity} CA ${zip}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <link rel="preload" as="image" href={heroImage} />

        {/* ========================= JSON-LD: WebPage ========================= */}
        {(() => {
          const base = 'https://modernbuildremodel.com';
          const svc = serviceData.link.replace('/services/', '');
          const absUrl = `https://modernbuildremodel.com/${svc}/california/${city}/${zip}`;
          return (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "WebPage",
                  "@id": `${absUrl}#webpage`,
                  "url": absUrl,
                  "name": contentWithLocation.meta.title,
                  "description": contentWithLocation.meta.description,
                  "inLanguage": "en-US",
                  "publisher": { "@id": `${base}#localbusiness` },
                  "mainEntity": { "@id": `${absUrl}#service` },
                }),
              }}
            />
          );
        })()}


        {/* ========================= JSON-LD: Service with Offers ========================= */}

        {(() => {
          const base = 'https://modernbuildremodel.com';
          const svc = serviceData.link.replace("/services/", "");
          const absUrl = `https://modernbuildremodel.com/${svc}/california/${city}/${zip}`;

          const schema = {
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${absUrl}#service`,
            "mainEntityOfPage": { "@id": `${absUrl}#webpage` },
            "name": `${serviceData.title} in ${city}, CA ${zip}`,
            "serviceType": svc.replace(/-/g, " "),
            "description": contentWithLocation.meta.description,
            "url": absUrl,
            "image": `https://modernbuildremodel.com${heroImage}`,
            "provider": { "@id": `${base}#localbusiness` },
            "areaServed": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": city,
                "addressRegion": "CA",
                "postalCode": zip,
                "addressCountry": "US"
              }
            },
            "offers": [
              {
                "@type": "Offer",
                "url": absUrl,
                "priceCurrency": "USD",
                "price": "0",
                "name": "Free Estimate",
                "description": "Get a free, no-obligation estimate for your project",
                "availability": "https://schema.org/InStock",
              },
              {
                "@type": "Offer",
                "url": `${absUrl}#financing`,
                "name": "Financing Available",
                "description": "Flexible financing options with $0 down",
                "availability": "https://schema.org/InStock"
              }
            ]
          };

          return (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
            />
          );
        })()}

        {reviewsByZip && reviewsByZip.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": reviewsByZip.map((review) => {
                const dateParts = review.date.trim().split(" ");
                const monthMap = {
                  Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
                  Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12"
                };

                const year = dateParts[1];
                const month = monthMap[dateParts[0]] || "01";
                const isoDate = `${year}-${month}-01`;

                return {
                  "@type": "Review",
                  "itemReviewed": {
                    "@id": "https://modernbuildremodel.com/#localbusiness"
                  },
                  "author": {
                    "@type": "Person",
                    "name": review.consumer
                  },
                  "reviewRating": {
                    "@type": "Rating",
                    "ratingValue": review.stars.toString(),
                    "bestRating": "5",
                    "worstRating": "1"
                  },
                  "reviewBody": review.content,
                  "datePublished": isoDate
                };
              })
            }),
          }}
        />
      )}


        {/* ========================= JSON-LD: FAQ ========================= */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateFAQSchema({ service: serviceData.title, city: formattedCity, zip, faqs: mergedFaqs })
            ),
          }}
        />

        {/* ========================= JSON-LD: BreadcrumbList ========================= */}
        {(() => {
          const base = 'https://modernbuildremodel.com';
          const svc = serviceData.link.replace('/services/', '');
          const itemUrl = `${base}/${svc}/california/${city}/${zip}`;
          return (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {
                      "@type": "ListItem",
                      "position": 1,
                      "name": "Modern Build Home Remodeling",
                      "item": { "@id": base, "@type": "WebPage" }
                    },
                    {
                      "@type": "ListItem",
                      "position": 2,
                      "name": "Service Areas",
                      "item": { "@id": `${base}/service-areas`, "@type": "WebPage" }
                    },
                    {
                      "@type": "ListItem",
                      "position": 3,
                      "name": serviceData.title,
                      "item": { "@id": `${base}/service-areas/${svc}`, "@type": "WebPage" }
                    },
                    {
                      "@type": "ListItem",
                      "position": 4,
                      "name": `${formattedCity}, CA ${zip}`,
                      "item": { "@id": itemUrl, "@type": "WebPage" }
                    }
                  ]
                })
              }}
            />
          );
        })()}

        {/* ========================= JSON-LD: HowTo (Permit steps) ========================= */}
        {(() => {
          const base = 'https://modernbuildremodel.com';
          const svc = serviceData.link.replace('/services/', '');
          const absUrl = `https://modernbuildremodel.com/${svc}/california/${city}/${zip}`;

          const formattedCity = city
            .split("-")
            .map(w => w[0].toUpperCase() + w.slice(1))
            .join(" ");

          const steps = (Array.isArray(permits) ? permits : [])
            .flatMap(item => [item.line1, item.line2, item.line3, item.line4, item.line5])
            .filter(Boolean)
            .slice(0, 5);

          const serviceTitle = serviceData.link.replace("/services/", "");

          let tools = [];
          let supplies = [];
          let totalTime = "P2W";
          let estimatedCost = 1500;

          if (serviceTitle.includes("complete-home-remodeling")) {
            tools = ["Building permit application", "Structural drawings", "Engineering report", "Inspection checklist"];
            supplies = ["Site plans", "Project specifications", "Contractor license copy"];
            totalTime = "P6W";
            estimatedCost = 3000;
          } else if (serviceTitle.includes("kitchen-remodeling") || serviceTitle.includes("bathroom-remodeling")) {
            tools = ["Building permit application", "Plumbing permit", "Electrical permit", "Inspection checklist"];
            supplies = ["Construction drawings", "Material specifications", "Contractor license copy"];
            totalTime = "P4W";
            estimatedCost = 1500;
          } else if (serviceTitle.includes("adu")) {
            tools = ["Building permit application", "Structural plans", "Engineering review", "Inspection checklist"];
            supplies = ["Site plans", "Material specifications", "Contractor license copy"];
            totalTime = "P6W";
            estimatedCost = 2500;
          } else if (serviceTitle.includes("outdoor-living") || serviceTitle.includes("pavers") || serviceTitle.includes("asphalt-shingles")) {
            tools = ["Zoning clearance form", "Building permit application", "Inspection checklist"];
            supplies = ["Material specifications", "Construction plans", "Contractor license copy"];
            totalTime = "P3W";
            estimatedCost = 1200;
          } else if (serviceTitle.includes("foundations") || serviceTitle.includes("earthquake-retrofit")) {
            tools = ["Structural permit application", "Soil test results", "Engineering plans", "Inspection checklist"];
            supplies = ["Concrete mix", "Steel rebar", "Anchor bolts"];
            totalTime = "P5W";
            estimatedCost = 2500;
          } else if (serviceTitle.includes("exterior-painting")) {
            tools = ["Surface preparation checklist", "Paint color approval form", "Safety data sheet"];
            supplies = ["Primer", "Exterior paint", "Protective gear"];
            totalTime = "P1W";
            estimatedCost = 200;
          } else if (serviceTitle.includes("energy-efficient-windows")) {
            tools = ["Energy compliance form", "Window schedule", "Manufacturer cut sheets"];
            supplies = ["Energy-efficient windows", "Sealant", "Mounting brackets"];
            totalTime = "P1W";
            estimatedCost = 250;
          } else if (serviceTitle.includes("solar-panels")) {
            tools = ["Electrical permit application", "Solar layout design", "Panel mounting guide"];
            supplies = ["Solar panels", "Inverter", "Conduit and wiring"];
            totalTime = "P3W";
            estimatedCost = 800;
          } else if (serviceTitle.includes("artificial-turf")) {
            tools = ["Site grading plan", "Drainage assessment", "Material order form"];
            supplies = ["Artificial turf rolls", "Base aggregate", "Weed barrier fabric"];
            totalTime = "P1W";
            estimatedCost = 200;
          } else if (serviceTitle.includes("hvac")) {
            tools = ["Mechanical permit form", "Load calculation sheet", "Duct layout plan"];
            supplies = ["HVAC unit", "Thermostat", "Air ducts"];
            totalTime = "P2W";
            estimatedCost = 500;
          } else if (serviceTitle.includes("tile-roof") || serviceTitle.includes("flat-roof") || serviceTitle.includes("metal-roof") || serviceTitle.includes("tpo-roof") || serviceTitle.includes("gutter")) {
            tools = ["Roofing permit application", "Inspection checklist", "Material specification sheet"];
            supplies = ["Roofing materials", "Underlayment", "Flashing", "Gutters"];
            totalTime = "P3W";
            estimatedCost = 1200;
          } else {
            tools = ["Building permit application", "Zoning clearance form", "Inspection checklist"];
            supplies = ["Site plans", "Project specifications", "Contractor license copy"];
            totalTime = "P2W";
            estimatedCost = 500;
          }

          return (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "@id": `${absUrl}#howto-permits-${city}-${zip}`,
                  "name": `Permit steps for ${serviceData.title} in ${formattedCity} ${zip}`,
                  "description": `A detailed, step by step guide on obtaining permits for ${serviceData.title} projects in ${formattedCity} ${zip}.`,
                  "totalTime": totalTime,
                  "estimatedCost": {
                    "@type": "MonetaryAmount",
                    "currency": "USD",
                    "value": estimatedCost
                  },
                  "tool": tools.map(t => ({ "@type": "HowToTool", "name": t })),
                  "supply": supplies.map(s => ({ "@type": "HowToSupply", "name": s })),
                  "step": steps.map((s, i) => ({
                    "@type": "HowToStep",
                    "position": i + 1,
                    "name": s.trim()
                  }))
                })
              }}
            />
          );
        })()}

      </Head>

      <Header />
      <Hero
        page="page"
        title={[`${serviceData.title}`, `${formattedCity}, CA ${zip}`]}
        subtitle={`Modern Build offers ${serviceData.title.toLowerCase()} services in ${formattedCity}, CA ${zip}`}
        button="Get a Free Estimate"
        buttonLink="#quick-quote-form"
        hideButton={true}
        backgroundImage={heroImage}
        hasContentH1={false}
      />

      <QuickQuoteForm serviceTitle={serviceData.title} city={city} zip={zip} />

      <Split
        {...{
          ...contentWithLocation.split,
          subtitle: contentWithLocation.split.subtitle,
          title: contentWithLocation.split.title,
          description: contentWithLocation.split.description,
          extraText: contentWithLocation.split.extraText,
          image1: injectDynamic(contentWithLocation.split.image1, true),
          image2: injectDynamic(contentWithLocation.split.image2, true),
        }}
        phone="(323) 300-4130"
      />

      {/* ✅ Render first server-side section */}
      <div dangerouslySetInnerHTML={{ __html: renderedSections[0] }} />

      {/* ✅ Render Benefits/Process (client-side, with icons) */}
      {groupBComponents[0]}

      {/* ✅ Render second server-side section */}
      <div dangerouslySetInnerHTML={{ __html: renderedSections[1] }} />

      {/* ✅ Render Benefits/Process (client-side, with icons) */}
      {groupBComponents[1]}

      {/* ✅ Render remaining server-side sections */}
      {renderedSections.slice(2).map((section, idx) => (
        <div key={idx + 2} dangerouslySetInnerHTML={{ __html: section }} />
      ))}

      <Split
        {...{
          ...contentWithLocation.secondSplit,
          subtitle: contentWithLocation.secondSplit.subtitle,
          title: contentWithLocation.secondSplit.title,
          description: contentWithLocation.secondSplit.description,
          extraText: contentWithLocation.secondSplit.extraText,
          image1: injectDynamic(contentWithLocation.secondSplit.image1, true),
          image2: injectDynamic(contentWithLocation.secondSplit.image2, true),
        }}
        phone="(323) 300-4130"
      />

      <MicroTestimonials
        service={serviceData.link}
        city={city}
        zipcode={zip}
        reviewsByZip={reviewsByZip}
      />

      {/*
        <Gallery images={galleryImages} />
      */}
      <LocalizedMessage
        formattedCity={formattedCity}
        zip={zip}
        localLine={localLine}
        serviceDataImage={serviceData.imageSrc}
        serviceData={serviceData}
      />

      {mergedFaqs.length > 0 && <FAQ category={contentWithLocation.faqCategory} city={city} zip={zip} faqs={mergedFaqs} />}

      {relatedServices?.length > 0 && (
        <div className="text-black text-center font-light bg-white leading-relaxed pt-2 px-20 mx-auto">
          <h3 className="text-2xl mb-4 text-center">Other Services We Offer in {formattedCity}, CA</h3>
          <ul className="flex flex-wrap justify-center gap-3 underline">
            {relatedServices.slice(0, 6).map((service, idx) => (
              <li key={idx}>
                <Link href={service.path} className="hover:text-primary transition" aria-label={`Explore ${service.title} in ${formattedCity}`}>
                  {service.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <RelatedLinks relatedLinks={relatedLinks} service={serviceData.link.replace('/services/', '')} />
      <ExternalResources city={city} zip={zip} serviceData={serviceData} />
      <ServiceBundles serviceData={serviceData} city={city} zip={zip} />

      <KnowledgeHub
        key="knowledgehub"
        currentPath={currentPath}
        serviceSlug={serviceType}
      />
      
      <div className="bg-white text-center pb-12">
        <Link
          href={serviceData.link}
          className="inline-block text-lg uppercase py-3 px-8 bg-primary text-white font-base hover:bg-black transition-all duration-500 ease-in-out"
          aria-label={`Discover our ${serviceData.title}`}
        >
          Discover our {serviceData.title}
        </Link>
      </div>

      <LocationMap city={formattedCity} zip={zip} />
      <SocialProof />
      <ContactUs />
      <Footer />
    </div>
  );
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

/* ========================== Optimized data access =========================== */

export async function getStaticProps({ params }) {
  const { service, city, zipcode, state } = params;

  const formattedCity = city
    .split('-')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');


  const slug = service;
  const serviceKey = slug;

  /* ================== 1. Validate Service ================== */
  const fullServiceData = allServices.find((s) =>
    s.link.endsWith(`/${slug}`)
  );

  if (!fullServiceData) {
    return { notFound: true };
  }

  const serviceData = dynamicServices.find((s) =>
    s.link.endsWith(`/${slug}`)
  );

  if (!serviceData) {
    return {
      redirect: {
        destination: fullServiceData.link,
        permanent: true,
      },
    };
  }

  /* ================== 2. Handle City / ZIP Logic ================== */
  const stateCoverage = geoCoverage?.[state] || {};
  const removedCitiesByState = geoRemovedCoverage?.[state] || {};

  // A. If city + ZIP exists in geoCoverage → load page
  if (stateCoverage[city]?.includes(zipcode)) {
    // valid city + ZIP, continue to load page
  } else if (removedCitiesByState[city]?.includes(zipcode)) {
    // B. City + ZIP exists in geoRemovedCoverage
    if (stateCoverage[city]?.length) {
      // 1. City still exists in geoCoverage → redirect to first ZIP
      return {
        redirect: {
          destination: `/${service}/california/${city}/${stateCoverage[city][0]}`,
          permanent: true,
        },
      };
    } else if (cityMergeMap[city]) {
      // 2. City is merged → redirect to merged city + ZIP
      const { city: mergedCity, zip: mergedZip } = cityMergeMap[city];
      return {
        redirect: {
          destination: `/${service}/california/${mergedCity}/${mergedZip}`,
          permanent: true,
        },
      };
    } else {
      // 3. Dead city → redirect to main service page
      return {
        redirect: {
          destination: fullServiceData.link,
          permanent: true,
        },
      };
    }
  } else {
    // ZIP not found in either geoCoverage or geoRemovedCoverage → redirect to main service page
    return {
      redirect: {
        destination: fullServiceData.link,
        permanent: true,
      },
    };
  }

  /* ================== 3. Continue Loading Page ================== */
  const rawContent = serviceContent[serviceKey];
  if (!rawContent) return { notFound: true };

  const content = injectLocation(rawContent, city, zipcode);
  if (!content) return { notFound: true };

  const galleryImages = getImages('home');

  const relatedServices = dynamicServices
    .filter((s) => s.link !== serviceData.link)
    .map((s) => ({
      title: s.title,
      path: `/${s.link.replace('/services/', '')}/california/${city}/${zipcode}`,
    }));

  const localLine = getDynamicLocalLine(
    city
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );

  const relatedLinks = getRelatedLinks(city, zipcode, geoCoverage);

  const costGuide = generateCostGuide(serviceKey, city, zipcode);

  const cache = {};

  const loadJsonFolder = (folderPath) => {
    if (cache[folderPath]) return cache[folderPath];
    const absolutePath = path.join(process.cwd(), "data", folderPath);
    if (!fs.existsSync(absolutePath)) return (cache[folderPath] = {});
    const files = fs.readdirSync(absolutePath);
    const data = {};
    for (const file of files) {
      if (file.endsWith(".json")) {
        const key = file.replace(".json", "");
        data[key] = JSON.parse(fs.readFileSync(path.join(absolutePath, file), "utf-8"));
      }
    }
    cache[folderPath] = data;
    return data;
  };

  const reviewsByCategory = loadJsonFolder('reviewsByZip');
  const permitStepsByCategory = loadJsonFolder('permits');
  const whyChooseUsByCategory = loadJsonFolder('whyChooseUsParagraphs');
  const localGuideByCategory = loadJsonFolder('localGuideParagraphs');
  const localInsightByCategory = loadJsonFolder('localInsightParagraphs');
  const faqsByCategory = loadJsonFolder('faqsByServiceCityZip');

  const permitStepsForZip = permitStepsByCategory[serviceKey]?.[`${serviceKey}-${city}-${zipcode}`] || [];
  const chooseUsForZip = whyChooseUsByCategory[serviceKey]?.[`${serviceKey}-${city}-${zipcode}`] || [];
  const localGuideForZip = localGuideByCategory[serviceKey]?.[`${serviceKey}-${city}-${zipcode}`] || [];
  const localInsightForZip = localInsightByCategory[serviceKey]?.[`${serviceKey}-${city}-${zipcode}`] || [];
  const reviewsForZip = reviewsByCategory[serviceKey]?.[`${serviceKey}-${zipcode}`] || [];
  const dynamicFaqs = faqsByCategory[serviceKey]?.[`${serviceKey}-${city}-${zipcode}`] || [];

  let categoryFaqs = [];
  try {
    const mod = require(`../../../../data/faq/${content.faqCategory}`);
    categoryFaqs = mod?.default || [];
  } catch {
    categoryFaqs = [];
  }

  const formatCityName = (c) =>
    c ? c.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

  const injectLocationInFAQ = (faqList = [], c, z) => {
    const formattedCity = formatCityName(c);
    const hasLocation = Boolean(formattedCity && z);
    return faqList.map(({ question = '', answer = '' }) => {
      let q = question;
      let a = answer;
      if (hasLocation) {
        q = q.replace(/<IN_CITY_ZIP>/g, ` in ${formattedCity}, CA ${z}`).replace(/<IN_CITY>/g, ` in ${formattedCity}`);
        a = a.replace(/<IN_CITY_ZIP>/g, ` in ${formattedCity}, CA ${z}`).replace(/<IN_CITY>/g, ` in ${formattedCity}`);
      } else {
        q = q.replace(/[\s,]*<IN_CITY_ZIP>/g, '').replace(/[\s,]*<IN_CITY>/g, '');
        a = a.replace(/[\s,]*<IN_CITY_ZIP>/g, '').replace(/[\s,]*<IN_CITY>/g, '');
      }
      return { question: q.trim(), answer: a.trim().replace(/\s+/g, ' ') };
    });
  };

  const mergedFaqs = [
    ...injectLocationInFAQ(dynamicFaqs, city, zipcode),
    ...injectLocationInFAQ(categoryFaqs, city, zipcode),
  ];


// ✅ PRE-RENDER SECTIONS SERVER-SIDE (WITHOUT Benefits & ProcessRoadmap)
const ReactDOMServer = require('react-dom/server');
const React = require('react');

const baseSeed = `${serviceKey}-${city}-${zipcode}`;

const seoBlock = [
  React.createElement(CostIntro, { key: "costintro", service: serviceData.title, city, zip: zipcode, data: costGuide }),
  React.createElement(CostTable, { key: "costtable", data: costGuide, city, zip: zipcode }),
  React.createElement(Factors, { key: "factors", service: serviceKey, city, zip: zipcode, data: costGuide }),
  React.createElement(Comparison, { key: "comparison", service: serviceKey, data: costGuide, city }),
  React.createElement(Formula, { key: "formula", data: costGuide, city }),
  React.createElement(Decision, { key: "decision", data: costGuide, city, zip: zipcode, service: serviceKey })
];

// ❌ REMOVE Benefits & ProcessRoadmap from server-side rendering
// They need iconMap which can't be serialized

const groupA = shuffleGroupBySeed(baseSeed + 'A', [
  React.createElement(ChooseUs, {
    key: "choose",
    serviceData,
    city,
    zip: zipcode,
    whyChooseUsParagraphs: chooseUsForZip
  }),
  React.createElement(ContentBlock, {
    key: "content",
    ...content.contentBlock,
    background: "white"
  }),
  React.createElement(ZipIntro, {
    key: "zipintro",
    zip: zipcode,
    city,
    serviceData
  }),
  React.createElement(LocalGuide, {
    key: "localguide",
    serviceData,
    city,
    zip: zipcode,
    localGuideParagraphs: localGuideForZip
  })
]);

// ✅ Don't create groupB with React.createElement - we'll render it client-side
// Just pass the order as data
const groupBOrder = flipBySeed(baseSeed + 'B', 'benefits', 'process');

const groupC = flipBySeed(
  baseSeed + 'C',
  React.createElement(LocalInsights, {
    key: "insights",
    serviceData,
    city,
    zip: zipcode,
    localInsightParagraphs: localInsightForZip
  }),
  React.createElement(PermitHowTo, {
    key: "permit",
    serviceData,
    city,
    zip: zipcode,
    permits: permitStepsForZip
  })
);

const sections = [];

sections.push(groupA[0]);
// ✅ groupB will be rendered client-side, so we skip it here
sections.push(groupA[1]);
// ✅ groupB will be rendered client-side, so we skip it here
sections.push(groupA[2]);
sections.push(groupC[0]);

sections.push(...seoBlock);

sections.push(
  React.createElement(CTASection, {
    key: "cta",
    title: "Remodel Now. Pay Later.",
    subtitle: "100% Financing Available with $0 Down*",
    buttonText: "Get Pre Approved Today",
    link: "/contact",
    backgroundImage: "/hero/best-home-remodling-contractor-in-los-angeles.webp"
  })
);

sections.push(groupC[1]);
sections.push(groupA[3]);

// ✅ RENDER TO HTML STRING
const renderedSections = sections.map(section => 
  ReactDOMServer.renderToStaticMarkup(section)
);

return {
  props: {
    serviceData,
    serviceTitle: serviceData.title,
    content,
    galleryImages,
    city,
    zip: String(zipcode),
    relatedServices,
    localLine,
    relatedLinks,
    reviewsByZip: reviewsForZip,
    permits: permitStepsForZip,
    whyChooseUsParagraphs: chooseUsForZip,
    localGuideParagraphs: localGuideForZip,
    localInsightParagraphs: localInsightForZip,
    mergedFaqs,
    costGuide,
    renderedSections,
    groupBOrder, // ✅ Pass the order for client-side rendering
  },
  revalidate: 86400,
};
}
